/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.16.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';

import { LOAD_AUTH_DATA_SUCCESSFUL, LOGIN_PROCESS_SUCCESSFUL } from '@pages/PageAuth/constants/authConstants';
import { makeSelectAuthoritiesIsServiceUser } from '@pages/PageAuth/selectors/authSelectors';

import { requestSaga } from '../../sagas/requestSaga';
import { API_PERSONAL_DATA_CONFIRM_CHECK } from '../app/Api';
import { modalOpen } from '../modal/modalActions';
import { ONBOARDING_FINISH } from '../onboarding/onboardingConstants';
import { CHECK_GUARD_SUCCESSFUL } from '../role/constants/actionConstants';
import { AccessComponentKeyEnum } from '../role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '../role/selectors/guardSelector';
import PersonalDataConfirmModal from './components/PersonalDataConfirmModal';
import { IPersonalDataConfirmCheckResponse, mapPersonalDataConfirmCheck } from './personalDataConfirmMapping';
import { IPersonalDataConfirm } from './personalDataConfirmTypes';

export function* personalDataConfirmCheckSaga(): SagaIterator {
    try {
        yield take(CHECK_GUARD_SUCCESSFUL);

        const isOnboardingEnabled = yield select(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.onboarding));
        const isServiceUser = yield select(makeSelectAuthoritiesIsServiceUser());
        if (isOnboardingEnabled && !isServiceUser) {
            yield take(ONBOARDING_FINISH);
        }

        const isPersonalDataConfirmCheckEnabled = yield select(
            makeSelectGuardComponentEnabled(AccessComponentKeyEnum.personalDataConfirmCheck),
        );
        if (!isPersonalDataConfirmCheckEnabled) {
            return;
        }

        const request: IPersonalDataConfirmCheckResponse = yield call(
            requestSaga,
            API_PERSONAL_DATA_CONFIRM_CHECK,
            'GET',
        );

        const data: IPersonalDataConfirm = mapPersonalDataConfirmCheck(request);
        if (data.subscribersNotConfirmed) {
            yield put(
                modalOpen({
                    modalParams: {
                        closeable: false,
                    },
                    render: (props) =>
                        createElement(PersonalDataConfirmModal, {
                            ...data,
                            onCloseModal: props.onCloseModal,
                        }),
                }),
            );
        }
    } catch (err) {
        console.error(err);
    }
}

export function* personalDataConfirmSagas(): SagaIterator {
    yield all([takeLatest([LOAD_AUTH_DATA_SUCCESSFUL, LOGIN_PROCESS_SUCCESSFUL], personalDataConfirmCheckSaga)]);
}
